
import { defineComponent } from '@vue/composition-api';
import IllinoisIcons from './icons.vue';

export default defineComponent({
  name: 'IllinoisDatePicker',
  components: {
    IllinoisIcons
  },
  props: {
    id: {
      type: String,
      default: () => `datepicker-${Math.random().toString(36).substr(2, 9)}`
    },
    label: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      currentDate: new Date(),
      dropdownStyle: {} as Record<string, string>,
      weekDays: ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
    };
  },
  computed: {
    displayDate(): string {
      if (!this.value) return '';
      const date = this.parseDate(this.value);
      if (!date) return '';
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    monthYearLabel(): string {
      return this.currentDate.toLocaleDateString('it-IT', {
        month: 'long',
        year: 'numeric'
      });
    },
    calendarDays(): (number | null)[] {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDayOfWeek = firstDay.getDay();
      const adjustedStartingDay =
        startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

      const days: (number | null)[] = [];

      for (let i = 0; i < adjustedStartingDay; i++) {
        days.push(null);
      }

      for (let i = 1; i <= daysInMonth; i++) {
        days.push(i);
      }

      const totalCells = 42;
      while (days.length < totalCells) {
        days.push(null);
      }

      return days;
    }
  },
  watch: {
    value: {
      handler(newValue: string) {
        if (newValue) {
          const date = this.parseDate(newValue);
          if (date) {
            this.currentDate = new Date(date.getFullYear(), date.getMonth(), 1);
          }
        }
      },
      immediate: true
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleResize, true);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleResize, true);
  },
  methods: {
    toggleDropdown(): void {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        if (this.value) {
          const date = this.parseDate(this.value);
          if (date) {
            this.currentDate = new Date(date.getFullYear(), date.getMonth(), 1);
          }
        }
        this.$nextTick(() => {
          this.calculateDropdownPosition();
        });
      }
    },
    selectDate(day: number | null): void {
      if (day === null) return;

      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const selectedDate = new Date(year, month, day);
      const formattedDate = this.formatDate(selectedDate);

      this.$emit('input', formattedDate);
      this.$emit('change', formattedDate);
      this.isOpen = false;
    },
    previousMonth(): void {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() - 1,
        1
      );
    },
    nextMonth(): void {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() + 1,
        1
      );
    },
    getDayClasses(day: number | null): Record<string, boolean> {
      if (day === null) {
        return {
          'text-illinois-black-10': true
        };
      }

      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      const dayDate = new Date(year, month, day);
      const formattedDay = this.formatDate(dayDate);
      const isSelected = this.value === formattedDay;
      const isToday = this.isToday(dayDate);

      const classes: Record<string, boolean> = {
        'hover:bg-illinois-black-5': !isSelected
      };

      if (isSelected) {
        classes['text-illinois-primary'] = true;
        classes['bg-illinois-black-5'] = true;
        classes['font-semibold'] = true;
      } else if (isToday) {
        classes['text-illinois-primary'] = true;
        classes['font-medium'] = true;
      } else {
        classes['text-illinois-black'] = true;
        classes['font-normal'] = true;
      }

      return classes;
    },
    isToday(date: Date): boolean {
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    },
    formatDate(date: Date): string {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    parseDate(dateString: string): Date | null {
      if (!dateString) return null;
      const parts = dateString.split('-');
      if (parts.length !== 3) return null;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return new Date(year, month, day);
    },
    handleClickOutside(event: Event): void {
      const container = this.$refs.datePickerContainer as HTMLElement;
      if (container && !container.contains(event.target as Node)) {
        this.isOpen = false;
      }
    },
    calculateDropdownPosition(): void {
      const container = this.$refs.datePickerContainer as HTMLElement;
      const calendar = this.$refs.calendarContainer as HTMLElement;
      if (!container || !calendar) return;

      const containerRect = container.getBoundingClientRect();
      const calendarRect = calendar.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - containerRect.bottom;
      const spaceAbove = containerRect.top;

      if (spaceBelow >= calendarRect.height || spaceBelow > spaceAbove) {
        this.dropdownStyle = {
          top: '100%',
          left: '0',
          marginTop: '0.25rem'
        };
      } else {
        this.dropdownStyle = {
          bottom: '100%',
          left: '0',
          marginBottom: '0.25rem'
        };
      }
    },
    handleResize(): void {
      if (this.isOpen) {
        this.calculateDropdownPosition();
      }
    }
  }
});
